import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { MainLayout } from '@/shared/layouts/ui/MainLayout';
import { Feedback } from '@/features/feedback/ui/Feedback';
import { MainSection } from './sections/MainSection';
import { SpecsSection } from './sections/SpecsSection';
import LinkArrowIcon from '@/assets/icons/linkArrow.svg?react';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  selectBuildingByDirection,
  selectBuildingById,
  selectBuildingsError,
  selectBuildingsIsLoading,
} from '@/entities/knowledge/model/selectors';
import { StateMessage } from '@/shared/ui/StateMessage';
import { Button } from '@/shared/ui/Button';
import { Typography } from '@/shared/ui/Typography/Typography';
import { TakePresentation } from '@/widgets/take_presentation/ui/TakePresentation';
import { TakeContacts } from '@/features/take_contacts/ui/TakeContacts';
import { addtionalBuildingTypes, Map } from '@/widgets/map/ui/Map';
import { KnowledgeMapCard } from '@/shared/ui/Card/KnowledgeMapCard';
import { HorizontalSlider } from '@/widgets/slider/ui/HorizontalSlider';
import { SwiperSlide } from 'swiper/react';
import { FloorPlansDownload } from '@/features/floor_plans_download/ui/FloorPlansDownload';
import { FloorPlanSlider } from '@/widgets/slider/ui/FloorPlanSlider';
import { openFeedBackModal } from '@/shared/ui/Modal/model/slice';

export default function KnowledgePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const loading = useAppSelector(selectBuildingsIsLoading);
  const error = useAppSelector(selectBuildingsError);

  const buildingSelectorById = useMemo(() => {
    return id ? selectBuildingById(id) : () => undefined;
  }, [id]);

  const building = useAppSelector(buildingSelectorById);

  const buildingSelectorByDirection = useMemo(() => {
    return id ? selectBuildingByDirection(id) : () => undefined;
  }, [id]);

  const buildings = useAppSelector(buildingSelectorByDirection);

  const dispatch = useAppDispatch();

  const handleShowApplicationDownloadModalWindow = () => {
    dispatch(
      openFeedBackModal({
        type: 'application',
      })
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <StateMessage
          title="Загружаем объект"
          message="Получаем информацию о выбранном объекте недвижимости."
        />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <StateMessage
          title="Не удалось загрузить объект"
          message={error}
          action={
            <Button
              text="Вернуться на главную"
              bg="black"
              className="px-6 py-3"
              onClick={() => navigate('/')}
            />
          }
        />
      </MainLayout>
    );
  }

  if (!building) {
    return (
      <MainLayout>
        <StateMessage
          title="Объект не найден"
          message="Похоже, объект недоступен или был удалён. Выберите другой объект на карте."
          action={
            <Button
              text="К каталогу объектов"
              bg="black"
              className="px-6 py-3"
              onClick={() => navigate('/map')}
            />
          }
        />
      </MainLayout>
    );
  }

  const handleScrollToFeedback = () => {
    const el = document.getElementById('feedback');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#feedback');
    }
  };

  const detailImages = useMemo(() => {
    if (!building.detailImages || building.detailImages.length == 0)
      return ['/images/knowledge_card_default_bg.jpg'];
    else return building.detailImages;
  }, [building]);

  return (
    <MainLayout className="mx-0 w-full max-w-none !px-0">
      <MainSection
        building={building}
        handleApplicationBtn={handleShowApplicationDownloadModalWindow}
      />

      <section className="w-full py-[20px] xl:py-[32px]">
        <div className="container flex flex-col gap-[20px] px-[10px] md:px-[32px] xl:gap-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <div className="flex flex-col gap-[20px] md:flex-row xl:gap-[32px]">
            <div
              className="flex h-[160px] w-full rounded-[20px] bg-cover bg-center md:h-[inherit] md:min-w-[380px] md:max-w-[380px]"
              style={{
                backgroundImage: building.aboutObject.images.url1
                  ? `url(${building.aboutObject.images.url1})`
                  : 'url(/images/knowledge_default_bg.png)',
              }}
            />
            <div className="flex w-full flex-col gap-[20px] md:w-auto xl:gap-[32px]">
              <Typography className="text-[24px] font-[700] uppercase xl:text-[26px]">
                {building.aboutObject.title}
              </Typography>
              <Typography className="text-[16px] font-[400]">
                {building.aboutObject.description.text1}
              </Typography>
              <Typography className="">
                {building.aboutObject.description.text2}
              </Typography>
            </div>
          </div>

          <div className="flex flex-col gap-[20px] md:flex-row xl:gap-[32px]">
            <div className="flex w-full flex-col gap-[20px] md:w-[380px] xl:gap-[32px]">
              <Typography className="text-[16px] font-[400] text-[grey]">
                {building.aboutObject.description.text3}
              </Typography>

              <Button
                text="Заказать звонок"
                bg="black"
                className="h-[42px] w-full pl-[24px] pr-[6px] text-[16px]"
                onClick={handleShowApplicationDownloadModalWindow}
                endIcon={<LinkArrowIcon className="h-[32px] w-[32px]" />}
              />
            </div>

            <div
              className="flex h-[140px] rounded-[20px] bg-cover bg-center md:h-auto md:flex-1"
              style={{
                backgroundImage: building.aboutObject.images.url2
                  ? `url(${building.aboutObject.images.url2})`
                  : 'url(/images/knowledge_default_bg.png)',
              }}
            />
          </div>
        </div>
      </section>

      <section className="w-full py-[20px] xl:py-[32px]">
        <div className="container flex flex-col gap-[20px] px-[10px] md:px-[32px] xl:gap-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <TakeContacts />
        </div>
      </section>

      <SpecsSection building={building} />

      <TakePresentation
        className="pb-[32px] pt-0 xl:pb-[56px] xl:pt-[32px]"
        presentationLink={building.presentation}
      />

      <Map className="h-[450px] md:h-[450px]" buildings={buildings || []} />

      <section className="w-full pb-[20px] pt-[32px] xl:pb-[32px] xl:pt-[56px]">
        <div className="container px-[10px] md:px-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <div className="shadow-cls flex flex-col gap-[20px] rounded-[30px] px-[12px] py-[24px] md:gap-[24px] md:px-[20px] md:py-[32px] xl:gap-[28px] xl:px-[56px]">
            <Typography className="w-full text-center text-[24px] uppercase xl:text-[26px]">
              Планировки
            </Typography>

            <div className="flex flex-col gap-[12px] md:h-[282px] md:flex-row md:gap-[40px]">
              <FloorPlanSlider slides={building.floorPlaning.planImages} />

              <img
                src={
                  building.floorPlaning.imageUrl ||
                  '/images/plan_default_img.png'
                }
                className="hidden h-[120px] w-full rounded-[20px] object-cover md:block md:h-full md:w-auto md:flex-1"
                alt=""
              />
            </div>

            <FloorPlansDownload url={building.floorPlaning.albumLink} />
          </div>
        </div>
      </section>

      <section className="w-full py-[20px] md:py-[32px]">
        <div className="container flex flex-col gap-[24px] px-[10px] md:px-[32px] xl:gap-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <div className="flex flex-col gap-[16px] xl:gap-[20px]">
            <Typography className="text-[24px] xl:text-[26px]">
              KRASOTA TOWERS в деталях
            </Typography>
            <Typography className="text-[16px] ">
              Фасады, интерьеры и инфраструктура — изучите объект на реальных
              фото
            </Typography>
          </div>

          <HorizontalSlider>
            {detailImages.map((item, index) => (
              <SwiperSlide
                key={index}
                className="h-[400px] overflow-hidden xl:h-[500px]"
              >
                <img src={item} alt="" className="h-full w-full object-cover" />
              </SwiperSlide>
            ))}
          </HorizontalSlider>
        </div>
      </section>

      <section className="w-full py-[20px] md:py-[32px]">
        <div className="container px-[10px] md:px-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <Feedback />
        </div>
      </section>

      <section className="w-full py-[32px]">
        <div className="container relative flex flex-col px-[10px] md:px-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <Typography className="text-[24px] font-bold uppercase xl:text-[26px]">
            Посмотрите ПОХОЖИЕ предложения
          </Typography>

          {!buildings || buildings.length <= 0 ? (
            <div className="flex w-full items-center justify-center py-[32px]">
              <Typography className="text-[14px] text-gray-400">
                Нет похожих объектов
              </Typography>
            </div>
          ) : (
            <div className="relative flex w-full flex-row gap-[20px] overflow-x-scroll py-[20px] xl:gap-[32px] xl:py-[32px]">
              {buildings?.map((item) => {
                const additional =
                  addtionalBuildingTypes[
                    building.direction as keyof typeof addtionalBuildingTypes
                  ];
                return (
                  <KnowledgeMapCard
                    key={item.id}
                    building={item}
                    className="z-10 max-w-[306px]"
                    additional={additional}
                  />
                );
              })}
            </div>
          )}

          <svg
            width="200"
            height="6"
            viewBox="0 0 200 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="self-end"
          >
            <path
              d="M200 2.88672L195 -3.26633e-05V5.77347L200 2.88672ZM0 2.88672V3.38672H195.5V2.88672V2.38672H0V2.88672Z"
              fill="black"
            />
          </svg>
        </div>
      </section>
    </MainLayout>
  );
}
